app.service("ExceptionAPI", [
	"$http",
	"ENV",
	"Session",
	"CurrentPatient",
	"browserDetector",
	"Base64",
	function ($http, ENV, Session, CurrentPatient, browserDetector,Base64) {
		var service = {
			isProcessing: false,
			lastUploadedKey: null,
			// Pulls the full backend error message out of a rejected $http response. The REST API reports
			// errors as { error: "..." } or { error: [ ... ] } (GlobalRestRequestExceptionHandler), and
			// Spring's own errors as { message: "...", error: "..." }; arrays are joined so nothing is lost.
			// Falls back to the status line when the body carries no message.
			backendErrorMessage: function (resp) {
				const data = resp && resp.data;
				const label = resp && resp.status ? `HTTP ${resp.status} error` : "API error";
				if (!data) {
					return label;
				}
				if (typeof data === "string") {
					return data;
				}
				const message = data.error || data.message || data.exceptionMessage;
				if (Array.isArray(message)) {
					return message.join("; ");
				}
				return message || label;
			},
			// The same message with the API's scaffolding taken off, for showing to a user rather than
			// auditing. A service failure reaches the browser wrapped by the REST layer's generic handler as
			// "Internal error while processing your request: [details: CattraxServiceException: <sentence> |
			// caused by: ...]". The sentence inside the wrapper is the actionable part - it is where the
			// funder's own answer ends up, such as "This eligibility check was rejected: the card number
			// and/or patient details submitted did not match Medicare checks" - and shown through the wrapper
			// it reads as a crash rather than as the answer it is.
			//
			// Null when the response carried no message of its own, so callers keep their own wording for
			// that case. backendErrorMessage() is left alone: the audit trail wants the whole thing.
			// The code the server put on an error to say which kind it was (e.g. ROOM_DOUBLE_BOOKED on a 409), so a
			// caller can tell two refusals with the same status apart without reading the message. Null when absent.
			serverCode: function (resp) {
				const data = resp && resp.data;
				return data && typeof data === "object" && typeof data.code === "string" ? data.code : null;
			},
			serverMessage: function (resp) {
				const data = resp && resp.data;
				if (!data) {
					return null;
				}
				// `message` first, then `error`. A server fault comes back with both - `error` holding the
				// HTTP status phrase ("Internal Server Error") and `message` the sentence that says what
				// actually happened, which on the ECLIPSE paths is the funder's own answer. Reading `error`
				// first, as the audit call above does, reports every one of them as "Internal Server Error".
				// The validation handler sends only `error`, so it is still read where there is no message.
				const candidates = typeof data === "string"
					? [data]
					: [data.message, data.error, data.exceptionMessage];
				const message = candidates
					.map(candidate => Array.isArray(candidate) ? candidate.join("; ") : candidate)
					.find(candidate => typeof candidate === "string" && candidate.trim());
				if (!message) {
					return null;
				}

				// The details block when there is one, and only up to the first cause: everything after
				// "caused by" is the stack of wrappers around the same failure, not more of the answer.
				const details = /\[details:\s*([\s\S]*)\]\s*$/.exec(message);
				let readable = (details ? details[1] : message).split(" | caused by: ")[0];
				// The exception class the wrapper names, which says nothing to the person reading it.
				readable = readable.replace(/^(?:[\w.$]*(?:Exception|Error)):\s*/, "").trim();
				return readable || message.trim();
			},
			uploadException: async function (message, method_context) {
				// Don't audit the same failure twice: a single API error is seen by both the HTTP
				// interceptor and the global $exceptionHandler, and the html2canvas screenshot below makes
				// each upload slow enough that the duplicate arrives while the first is still in flight.
				const key = `${message}|${method_context}`;
				if (key === this.lastUploadedKey) {
					return;
				}
				this.lastUploadedKey = key;
				this.isProcessing = true;
				try {
					return await this.postException(message, method_context);
				} finally {
					this.isProcessing = false;
				}
			},
			postException: async function (message, method_context) {
				const { userAgent, name, version, url } = browserDetector.getBrowserInfo();
				const data = {
					user_id: Session.userId,
					patient_id: CurrentPatient.patient?.id,
					practice_id: Session.practice?.id,
					exception_audit_type: "GENERIC_SERVER",
					message,
					method_context,
					url,
					user_agent: userAgent,
					browser_name: name,
					browser_version: version,
				};
				const canvas = await html2canvas(document.body, {
					windowWidth: document.documentElement.scrollWidth,
					windowHeight: document.documentElement.scrollHeight,
					scale: 0.75,
				});

				data.image = Base64.encode(canvas.toDataURL("image/png"));

				return $http({
					method: "POST",
					url: ENV.API + "/api/exception-audit",
					headers: {
						Authorization: "Bearer " + Session.token,
						// Errors are reported from the login page too, where no practice is selected yet.
						"X-PRACTICE-ID": `${(Session.practice && Session.practice.id) || ""}`,
					},
					data,
				});
			},
			fetchException: function (startDate, endDate, option) {
				let append = "";
				option = option || {};
			    // {practiceId: xx, userId: yy}
				if (option.userId) {
					append = `&userId=${encodeURIComponent(option.userId)}`;
				}

				if (option.practiceId) {
					append = `${append}&practiceId=${encodeURIComponent(option.practiceId)}`;
				}

				return $http({
					method: "GET",
					url: ENV.API + `/api/exception-audit?endDate=${encodeURIComponent(endDate)}&startDate=${encodeURIComponent(startDate)}${append}`,
					headers: {
						Authorization: "Bearer " + Session.token,
					},
				});
			},
			getExceptionDetail: function (auditId,dateCreated) {
				return $http({
					method: "GET",
					url: ENV.API + `/api/exception-audit/${encodeURIComponent(auditId)}?dateCreated=${encodeURIComponent(dateCreated)}`,
					headers: {
						Authorization: "Bearer " + Session.token,
					},
				});
			},
		};
		return service;
	},
]);
